import {FC, Fragment, useEffect, useMemo, useState} from 'react';
import {styled} from 'styled-components';
import {useSearch} from 'wouter';

import {ChampionSummary} from '@shared/api/app_api';
import {Lane} from '@shared/model';

import {NavButton} from '@shared-web/components/core/button';
import {notifyError} from '@shared-web/lib/notification';
import {useStringArrayQueryString} from '@shared-web/lib/use_query_string';

import {ChampionOverview} from '@src/components/champion_overview';
import {LaneOverview} from '@src/components/lane_overview';
import {LoadingScreen} from '@src/components/loading_screen';
import {apiCall} from '@src/lib/api';
import {Team} from '@src/lib/teams';

export interface ChampionInfo extends ChampionSummary {
  team: Team;
}

interface TeamComp {
  top?: ChampionSummary;
  jungle?: ChampionSummary;
  mid?: ChampionSummary;
  adc?: ChampionSummary;
  support?: ChampionSummary;
}
const roles: (keyof TeamComp)[] = ['top', 'jungle', 'mid', 'adc', 'support'];

const roleToLane: Partial<Record<keyof TeamComp, Lane>> = {
  top: Lane.Top,
  jungle: Lane.Jungle,
  mid: Lane.Mid,
  support: Lane.Bot,
};

function buildTeam(champions: ChampionSummary[]): TeamComp {
  const comp: TeamComp = {};
  const unusedChampions: ChampionSummary[] = [];

  // Assign champion to there role (unless duplicate)
  for (const champion of champions) {
    if (champion.role in comp) {
      unusedChampions.push(champion);
    } else {
      comp[champion.role as keyof TeamComp] = champion;
    }
  }

  // Assign remaining champions (coming from duplicates) to remaining roles
  for (const role of roles) {
    if (comp[role]) {
      continue;
    } else {
      const nextChampion = unusedChampions.shift();
      comp[role] = nextChampion;
    }
  }

  return comp;
}

export const TeamOverviewPage: FC = () => {
  const queryString = useSearch();
  const [teamBlue] = useStringArrayQueryString(Team.Blue, []);
  const [teamRed] = useStringArrayQueryString(Team.Red, []);

  const [championsSummary, setChampionsSummary] = useState<ChampionSummary[]>();

  // Fetch information about the teams champions
  useEffect(() => {
    apiCall('POST /gpt-team-description', {teamBlue, teamRed})
      .then(setChampionsSummary)
      .catch(notifyError);
    // Prefetch champion descriptions
    apiCall('POST /gpt-champion-description-prefetch', {
      championNames: [...teamBlue, ...teamRed],
    }).catch(() => {
      /** Ignore errors */
    });
  }, [teamBlue, teamRed]);

  // Compute the team informations and comp
  const {blue, red} = useMemo(() => {
    // Blue
    const blueChampions: ChampionSummary[] = [];
    for (const label of teamBlue) {
      const champion = championsSummary?.find(c => c.name === label);
      if (!champion) {
        continue;
      }
      blueChampions.push(champion);
    }

    // Red
    const redChampions: ChampionSummary[] = [];
    for (const label of teamRed) {
      const champion = championsSummary?.find(c => c.name === label);
      if (!champion) {
        continue;
      }
      redChampions.push(champion);
    }

    return {blue: buildTeam(blueChampions), red: buildTeam(redChampions)};
  }, [championsSummary, teamBlue, teamRed]);

  if (championsSummary === undefined) {
    return (
      <Wrapper>
        <LoadingScreen />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Grid>
        {roles.map(role => (
          <Fragment key={role}>
            <ChampionOverview team={Team.Blue} info={blue[role]} />
            <ChampionOverview team={Team.Red} info={red[role]} />
            {roleToLane[role] !== undefined ? (
              <LaneOverviewWrapper>
                <LaneOverview lane={roleToLane[role]} />
              </LaneOverviewWrapper>
            ) : (
              <></>
            )}
          </Fragment>
        ))}
      </Grid>
      <BackButton>
        <NavButton to={`/pick?${queryString}`} overrides={{fontSize: 20, fontWeight: 'bold'}}>
          BACK
        </NavButton>
      </BackButton>
      <GameMetaButton>
        <NavButton
          to={`/meta/gpt-4o-mini?${queryString}`}
          overrides={{fontSize: 20, fontWeight: 'bold'}}
        >
          GAME META
        </NavButton>
      </GameMetaButton>
    </Wrapper>
  );
};

TeamOverviewPage.displayName = 'TeamOverviewPage';

const Wrapper = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: black;
  z-index: 100;
  padding: 16px;
  overflow-y: auto;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 16px;
`;

const BackButton = styled.div`
  position: fixed;
  top: 8px;
  left: 8px;
  z-index: 100;
`;

const GameMetaButton = styled.div`
  position: fixed;
  bottom: 8px;
  right: 8px;
  z-index: 100;
`;

const LaneOverviewWrapper = styled.div`
  grid-column: span 2;
`;
